import {
  BookOpen,
  Briefcase,
  ClipboardList,
  FilePen,
  FileSignature,
  Film,
  GraduationCap,
  Layers3,
  LayoutGrid,
  Link2,
  LucideIcon,
  Magnet,
  Mail,
  Megaphone,
  Presentation,
  Radar,
  User,
  UserRound,
  UserRoundSearch,
} from 'lucide-react';

import { PATHS } from 'router/config';
// Прямо из модулей ролей, не из барреля `core/hooks`: константы читаются при
// загрузке модуля, и цикл импортов через баррель отдавал бы `undefined` —
// то есть «без ограничения».
import {
  COURSE_CARD_ROLES,
  DOFIN_ROLES,
  STUDENT_CARD_ROLES,
  TEACHER_PROFILE_ROLES,
} from 'core/hooks/Palette/paletteRoles';
import { LEAD_ROLES } from 'core/hooks/Crm/useLeadPermissions';
import { UserRole } from 'types';

import {
  dofinIntakeLocation,
  dofinOperatorLocation,
  fundingCandidateLocation,
  leadLocation,
  locationHref,
} from '../leadLinks';

/**
 * Что фронт знает про виды связей задачи (ТЗ 0064): иконка и маршрут.
 *
 * ПОДПИСЕЙ здесь нет: они живут в локалях (`pages.tasks.links.kind.*`), как и
 * все остальные слова экрана. Сервер решает, какие виды ДОСТУПНЫ роли, — это
 * его дело; но его собственные подписи идут через Django-переводы, и на стенде
 * это дало вперемешку «UMOWA», «COURSE» и «GRUPA».
 *
 * Первые четыре иконки — те же, что в палитре команд
 * (`CommandPaletteContainer`): один и тот же студент не должен выглядеть
 * по-разному на соседних экранах.
 */
export const LINK_ICONS: Record<string, LucideIcon> = {
  student: User,
  teacher: GraduationCap,
  group: LayoutGrid,
  course: BookOpen,
  lesson: Presentation,
  agreement: FileSignature,
  funding_operator: UserRound,
  funding_candidate: UserRoundSearch,
  funding_intake: Layers3,
  vacancy: Briefcase,
  survey: ClipboardList,
  lesson_recording: Film,
  // ТЗ 0088: тот же знак, что у раздела «Маркетинг» в шапке страницы.
  marketing_signal: Megaphone,
  // Задача #116б: тот же магнит, что у раздела «Лиды» в меню.
  kommo_lead: Magnet,
  // ТЗ 0104: находка наблюдателя Slack — радар, как у агента на странице
  // «ИИ-агент» и у метки «AI» задачи.
  ai_finding: Radar,
  // ТЗ 0122: задача из письма и договор на подпись.
  mail_thread: Mail,
  contract: FilePen,
};

/** Иконка вида; для неизвестного — нейтральное звено, а не пустое место. */
export const linkIcon = (kind: string): LucideIcon => LINK_ICONS[kind] || Link2;

/**
 * Виды, адрес которых собирает СЕРВЕР (`TaskLinkType.href`) и который ведёт
 * внутрь ERP: такая связь рисуется ссылкой роутера (`<Link to>`), а не
 * голым `href`. Сигнал маркетинга — раздел «Маркетинг» с фильтром сигнала:
 * вкладка, окно, канал, спуск — их знает только сервер.
 */
export const ROUTER_LINK_KINDS = ['marketing_signal'];

/**
 * Адрес сигнала маркетинга с сервера — только если это действительно раздел
 * «Маркетинг» (`PATHS.marketing` + строка запроса). Чужой хост,
 * `javascript:`, `//хост`, другой раздел — `null`: подпись без ссылки.
 * Сервер отдаёт `null` и сам — тому, кому раздел закрыт.
 */
export const marketingSignalHref = (
  href: string | null | undefined,
): string | null => {
  const value = (href ?? '').trim();
  if (value === PATHS.marketing) return value;
  return value.startsWith(`${PATHS.marketing}?`) ? value : null;
};

/**
 * Перепроверка F28: роли, у которых ЕСТЬ маршрут карточки вида. Связь задачи
 * видят все, кто видит задачу, а карточку — не все: лид Kommo у HR,
 * дофинансирование у HR, студент у HR, курс у финансиста и продаж… Ссылка без маршрута —
 * это 404 (или «нет доступа» за кликом). Вид без записи — без ограничения.
 */
export const LINK_ROUTE_ROLES: Readonly<
  Partial<Record<string, readonly UserRole[]>>
> = {
  student: STUDENT_CARD_ROLES,
  teacher: TEACHER_PROFILE_ROLES,
  // Перепроверка G4: карточка курса есть не у всех, кто видит «Обучение».
  course: COURSE_CARD_ROLES,
  funding_operator: DOFIN_ROLES,
  funding_candidate: DOFIN_ROLES,
  funding_intake: DOFIN_ROLES,
  kommo_lead: LEAD_ROLES,
};

/** Роль может открыть карточку вида (вид без ограничения — да). */
export const canFollowLink = (
  kind: string,
  roles: readonly string[],
): boolean => {
  const allowed = LINK_ROUTE_ROLES[kind];
  return !allowed || roles.some((role) => allowed.includes(role as UserRole));
};

/**
 * Маршрут к карточке связанного объекта — ТОЛЬКО из `PATHS` (у сигнала
 * маркетинга — адрес сервера, проверенный по `PATHS.marketing`).
 *
 * Путь, собранный шаблонной строкой мимо `PATHS`, даёт 404, невидимый для
 * `tsc`, сборки и тестов. Виды, у которых пригодного маршрута в ERP нет
 * (занятие, запись занятия), возвращают `null` — их подпись остаётся
 * некликабельной. Выдумывать URL нельзя: ссылка, ведущая в никуда, хуже её
 * отсутствия.
 *
 * Задача #114: оператор, кандидат и окно набора дофинансирования ведут в
 * карточку на странице дофина — построителями из `leadLinks`, теми же, что у
 * остальных экранов.
 */
export const linkHref = (
  kind: string,
  objectId: string,
  href?: string | null,
  /**
   * Роли зрителя (F28): без маршрута карточки вида — `null`, подпись без
   * ссылки. Не передан — без проверки (как раньше).
   */
  roles?: readonly string[],
): string | null => {
  // Сервер явно отдал пустой адрес — уважаем: ссылки нет.
  if (href === '') return null;
  if (roles && !canFollowLink(kind, roles)) return null;
  switch (kind) {
    case 'student':
      return `${PATHS.studentDetail.replace(':id', objectId)}?tab=tasks`;
    case 'teacher':
      return `${PATHS.teacherDetail.replace(':id', objectId)}?tab=tasks`;
    case 'group':
      return `${PATHS.groupDetail.replace(':id', objectId)}?tab=tasks`;
    case 'course':
      return PATHS.settingsCourseDetail.replace(':courseId', objectId);
    case 'funding_operator':
      return locationHref(dofinOperatorLocation(objectId));
    case 'funding_candidate':
      return locationHref(fundingCandidateLocation(objectId));
    case 'funding_intake':
      return locationHref(dofinIntakeLocation(objectId));
    case 'kommo_lead':
      return locationHref(leadLocation(objectId));
    case 'marketing_signal':
      return marketingSignalHref(href);
    default:
      return null;
  }
};
